import { StageCandidateActionType } from 'enums/stageAction';
import { AdjustmentQuestionnaireResponse } from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CheckboxGroup,
  CheckboxGroupItem,
} from '@spotted-zebra-uk/ui-components';
import {
  adjustmentsResponsesType,
  shouldRedirectToIntro,
  steps,
  TAdjustmentsFlowRoutes,
} from '../AccessibilityAdjustments.helpers';
import SupportEmailDisclaimer from '../SupportEmailDisclaimer';
import styles from './AccessibilityNeedsList.module.scss';

const AccessibilityNeedsList = ({
  flowRoutes,
  completedStep,
  tests,
  onSubmit,
  isLiveChatEnabled = true,
}: {
  flowRoutes: TAdjustmentsFlowRoutes;
  completedStep: steps;
  tests: StageCandidateActionType[];
  onSubmit: (values: adjustmentsResponsesType, step: steps) => void;
  /**
   * The company's "Display live chat to users" setting. When off, the copy
   * points candidates to email only.
   */
  isLiveChatEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [adjustments, setAdjustments] = useState<adjustmentsResponsesType>([]);
  const [error, setError] = useState('');

  const accessibilityNeedsCheckboxOptions = useMemo(() => {
    const options: {
      label: string;
      value: AdjustmentQuestionnaireResponse;
    }[] = [
      {
        label: t('accessibilityAdjustments.needsList.assistiveTechnology'),
        value: AdjustmentQuestionnaireResponse.AssistiveTechnology,
      },
      {
        label: t('accessibilityAdjustments.needsList.physicalChallenges'),
        value: AdjustmentQuestionnaireResponse.PhysicalChallenges,
      },
      {
        label: t(
          'accessibilityAdjustments.needsList.difficultyProcessingInformation'
        ),
        value: AdjustmentQuestionnaireResponse.DifficultyProcessingInformation,
      },
      {
        label: t(
          'accessibilityAdjustments.needsList.difficultyMaintainingFocus'
        ),
        value: AdjustmentQuestionnaireResponse.DifficultyMaintainingFocus,
      },
      {
        label: t(
          'accessibilityAdjustments.needsList.difficultyManagingAnxiety'
        ),
        value: AdjustmentQuestionnaireResponse.DifficultyManagingAnxiety,
      },
      {
        label: t(
          'accessibilityAdjustments.needsList.difficultyManagingFatigue'
        ),
        value: AdjustmentQuestionnaireResponse.DifficultyManagingFatigue,
      },
      {
        label: t('accessibilityAdjustments.needsList.auditoryProcessing'),
        value: AdjustmentQuestionnaireResponse.AuditoryProcessing,
      },
    ];

    if (tests.includes(StageCandidateActionType.AiInterviews)) {
      options.push({
        label: t(
          'accessibilityAdjustments.needsList.difficultySpokenCommunication'
        ),
        value: AdjustmentQuestionnaireResponse.DifficultySpokenCommunication,
      });
    }

    return options;
  }, [tests, t]);

  useEffect(() => {
    // Redirect to flow intro page on page refresh
    shouldRedirectToIntro(completedStep) &&
      navigate(flowRoutes.getStepUrl('intro'));
  }, [completedStep, flowRoutes, navigate]);

  const handleChange = (value: string[]) => {
    if (error) {
      setError('');
    }
    setAdjustments(value as adjustmentsResponsesType);
  };

  const handleGoBack = () => {
    navigate(flowRoutes.getStepUrl('intro'));
  };

  const handleSubmit = () => {
    if (!adjustments.length) {
      setError(t(`accessibilityAdjustments.needsList.error`));
      return;
    }
    onSubmit(adjustments, 'options');
    navigate(flowRoutes.getStepUrl('summary'));
  };

  return (
    <section
      className={styles.container}
      data-testid="accessibility-needs-list"
    >
      <h3 className={styles.title}>
        {t(`accessibilityAdjustments.needsList.title`)}
      </h3>
      <div className={styles.checkboxContainer}>
        <CheckboxGroup
          id="accessibility-needs-checkbox-group"
          value={adjustments}
          onChange={handleChange}
          hasError={Boolean(error)}
          error={error}
        >
          {accessibilityNeedsCheckboxOptions.map(({ label, value }) => {
            return (
              <CheckboxGroupItem
                key={value}
                data-testid={`accessibility-needs__need-option-${value}`}
                id={value}
                label={label}
                name={value}
              />
            );
          })}
        </CheckboxGroup>
      </div>
      <SupportEmailDisclaimer
        i18nPrefix="accessibilityAdjustments.needsList"
        isLiveChatEnabled={isLiveChatEnabled}
        testIdPrefix="accessibility-needs-list"
        className={styles.disclaimer}
      />
      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={handleGoBack}
          aria-label={t('accessibilityAdjustments.goBack')}
          data-testid="accessibility-needs-list__go-back-button"
        >
          {t('accessibilityAdjustments.goBack')}
        </Button>
        <Button
          onClick={handleSubmit}
          className={styles.cta}
          type="submit"
          aria-label={t('common.continue')}
          data-testid="accessibility-needs-list__submit-button"
        >
          {t('common.continue')}
        </Button>
      </div>
    </section>
  );
};

export default AccessibilityNeedsList;
